import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import axios from 'axios';
import { Progress } from '../components/ui/progress';
import {
  ChartLineUp,
  TrendUp,
  TrendDown,
  Fire,
  Star,
  Trophy,
  Books,
  Microphone,
  ChatCircle,
  Robot,
  Spinner,
  ArrowRight,
  GraduationCap,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import LevelFigureModal from '../components/LevelFigureModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_LABELS = {
  vocabulary: 'Vokabeln',
  grammar: 'Grammatik',
  translation: 'Übersetzung',
  listening: 'Hörverstehen',
  multiple_choice: 'Multiple Choice',
  fill_blank: 'Lückentext',
  unknown: 'Sonstige'
};

const MiniChart = ({ data }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.total), 1);
  const W = 720;
  const H = 220;
  const padLeft = 32;
  const padBottom = 30;
  const plotW = W - padLeft - 8;
  const plotH = H - padBottom - 10;
  const bw = plotW / data.length;
  const totalAll = data.reduce((s, d) => s + d.total, 0);
  const activeDays = data.filter(d => d.total > 0).length;
  return (
    <div>
      <div className="flex gap-4 flex-wrap mb-3">
        <div><p className="text-[11px] uppercase tracking-wider text-[#A8A29E] font-bold">Übungen</p><p className="font-heading text-2xl font-bold text-[#292524]">{totalAll}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-[#A8A29E] font-bold">Aktive Tage</p><p className="font-heading text-2xl font-bold text-[#292524]">{activeDays}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-[#A8A29E] font-bold">Bestes</p><p className="font-heading text-2xl font-bold text-[#292524]">{maxVal}</p></div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56 sm:h-64" data-testid="activity-chart">
        {/* Y-axis grid & labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, k) => {
          const y = 10 + plotH * (1 - t);
          const v = Math.round(maxVal * t);
          return (
            <g key={k}>
              <line x1={padLeft} y1={y} x2={W - 4} y2={y} stroke="#E7E5E4" strokeWidth="1" />
              <text x={padLeft - 4} y={y + 3} textAnchor="end" fill="#A8A29E" fontSize="10" fontFamily="DM Sans">{v}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = d.total > 0 ? Math.max(6, (d.total / maxVal) * plotH) : 0;
          const color = d.accuracy >= 80 ? '#22c55e' : d.accuracy >= 50 ? '#EAB308' : d.total > 0 ? '#AB1C24' : '#E7E5E4';
          const x = padLeft + i * bw;
          const y = 10 + plotH - h;
          const showLabel = data.length <= 14 || i % Math.ceil(data.length / 12) === 0 || i === data.length - 1;
          return (
            <g key={i}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onTouchStart={() => setHoverIdx(i)}
              onTouchEnd={() => setTimeout(() => setHoverIdx(null), 2000)}>
              <rect x={x + 1.5} y={y} width={Math.max(bw - 3, 3)} height={h} rx="3"
                fill={color} opacity={hoverIdx === i ? 1 : 0.9}
                style={{ cursor: 'pointer' }} />
              {d.total > 0 && bw > 18 && (
                <text x={x + bw / 2} y={y - 4} textAnchor="middle" fill="#292524" fontSize="11" fontWeight="700" fontFamily="DM Sans">{d.total}</text>
              )}
              {showLabel && (
                <text x={x + bw / 2} y={H - 10} textAnchor="middle" fill="#78716C" fontSize="10" fontFamily="DM Sans">
                  {d.date.slice(5)}
                </text>
              )}
            </g>
          );
        })}
        {hoverIdx !== null && (() => {
          const d = data[hoverIdx];
          const x = padLeft + hoverIdx * bw + bw / 2;
          const tx = Math.max(60, Math.min(x, W - 60));
          return (
            <g>
              <rect x={tx - 58} y={12} width="116" height="42" rx="6" fill="#292524" />
              <text x={tx} y={28} textAnchor="middle" fill="white" fontSize="12" fontWeight="700" fontFamily="DM Sans">{d.date}</text>
              <text x={tx} y={45} textAnchor="middle" fill="white" fontSize="11" fontFamily="DM Sans">{d.total} Übungen · {d.accuracy}%</text>
            </g>
          );
        })()}
      </svg>
      <div className="flex items-center gap-3 mt-3 flex-wrap text-xs text-[#57534E]">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#22c55e] inline-block" /> ≥80%</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#EAB308] inline-block" /> 50-79%</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#AB1C24] inline-block" /> &lt;50%</span>
      </div>
    </div>
  );
};

const SkillBar = ({ label, accuracy, total }) => (
  <div className="space-y-1.5" data-testid={`skill-${label}`}>
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#292524] font-medium">{SKILL_LABELS[label] || label}</span>
      <span className="text-[#78716C]">{accuracy}% ({total})</span>
    </div>
    <div className="h-2.5 bg-[#F5F5F4] rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${accuracy}%`, backgroundColor: accuracy >= 80 ? '#22c55e' : accuracy >= 50 ? '#EAB308' : '#AB1C24' }}
      />
    </div>
  </div>
);

const LernanalysePage = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [showActivity, setShowActivity] = useState(true);
  const [loading, setLoading] = useState(true);
  const [figureModalOpen, setFigureModalOpen] = useState(false);
  const [modalLevelInfo, setModalLevelInfo] = useState(null);

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/lernanalyse`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Spinner size={32} className="animate-spin text-[#AB1C24]" />
    </div>
  );

  if (!data) return <p className="text-center text-[#78716C] py-12">Keine Daten verfügbar</p>;

  const { summary, progress_chart, skills, pedagogy } = data;
  const trendIcon = summary.week_trend >= 0 ? TrendUp : TrendDown;
  const trendColor = summary.week_trend >= 0 ? 'text-green-600' : 'text-red-500';

  return (
    <div className="space-y-6" data-testid="lernanalyse-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
        <ChartLineUp size={28} weight="duotone" className="text-[#0EA5E9]" />
        {lang === 'es' ? 'Análisis' : 'Analyse'}
      </h1>
        <p className="text-[#57534E] mt-1">Dein Fortschritt im Überblick — letzte 30 Tage</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Books, label: 'Übungen', value: summary.total_exercises, sub: `${summary.accuracy}% richtig`, color: '#AB1C24' },
          { icon: Fire, label: 'Streak', value: `${summary.streak} Tage`, sub: summary.streak >= 7 ? 'Weiter so!' : 'Bleib dran!', color: '#F97316' },
          { icon: Star, label: 'Punkte', value: summary.points, sub: `Lvl ${summary.level?.level} ${summary.level?.name}`, color: '#EAB308' },
          { icon: trendIcon, label: 'Diese Woche', value: summary.this_week_exercises, sub: `${summary.week_trend >= 0 ? '+' : ''}${summary.week_trend} vs. letzte Woche`, color: summary.week_trend >= 0 ? '#22c55e' : '#EF4444' },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#D6D3D1] p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${card.color}12` }}>
                <card.icon size={20} style={{ color: card.color }} weight="duotone" />
              </div>
              <span className="text-sm text-[#78716C]">{card.label}</span>
            </div>
            <p className="text-2xl font-bold text-[#292524]">{card.value}</p>
            <p className="text-xs text-[#78716C] mt-0.5">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Level Progress */}
      {summary.level && (
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-6" data-testid="level-progress">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => { setModalLevelInfo(summary.level); setFigureModalOpen(true); }}
              className="flex items-center gap-3 hover:bg-[#FAF9F6] rounded-xl p-2 -m-2 transition-colors cursor-pointer"
              title="Mehr über diese Person erfahren"
              data-testid="current-level-btn"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: summary.level.color }}>
                <Trophy size={22} className="text-white" weight="fill" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#292524]">Level {summary.level.level} — {summary.level.name}</p>
                {user?.role && !['admin', 'teacher'].includes(user.role) && (
                  <p className="text-xs text-[#78716C]">Spanisch-Niveau: {user?.spanish_level}</p>
                )}
              </div>
            </button>
            {summary.level.next_level && (
              <button
                type="button"
                onClick={() => { setModalLevelInfo(summary.level.next_level); setFigureModalOpen(true); }}
                className="text-right hover:bg-[#FAF9F6] rounded-xl p-2 -m-2 transition-colors cursor-pointer"
                title="Mehr über die nächste Person erfahren"
                data-testid="next-level-btn"
              >
                <p className="text-sm text-[#78716C]">Nächstes Level</p>
                <p className="font-semibold text-[#292524]">{summary.level.next_level.name} ({summary.level.next_level.min_points} Pkt)</p>
              </button>
            )}
          </div>
          <Progress value={summary.level.progress} className="h-3" />
          <p className="text-xs text-[#78716C] mt-2 text-center">{summary.level.progress}% zum nächsten Level</p>
        </div>
      )}

      {/* Activity Chart */}
      <div className="bg-white rounded-xl border border-[#D6D3D1] p-6" data-testid="activity-section">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-heading font-semibold text-[#292524]">Tägliche Aktivität</h3>
            <p className="text-sm text-[#78716C]">Übungen pro Tag — Farben nach Genauigkeit. Tippe auf einen Balken für Details.</p>
          </div>
          <button
            onClick={() => setShowActivity(v => !v)}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-stone-100 text-[#57534E]"
            title={showActivity ? 'Aktivität verbergen' : 'Aktivität anzeigen'}
            data-testid="activity-toggle"
          >
            {showActivity ? <Eye size={18} weight="duotone" /> : <EyeSlash size={18} weight="duotone" />}
          </button>
        </div>
        {showActivity ? <MiniChart data={progress_chart} />
          : <p className="text-sm text-[#A8A29E] italic text-center py-8">Die Aktivität ist ausgeblendet.</p>}
      </div>

      {/* Skills Breakdown */}
      <div className="bg-white rounded-xl border border-[#D6D3D1] p-6" data-testid="skills-section">
        <h3 className="font-heading font-semibold text-[#292524] mb-1">Fähigkeiten</h3>
        <p className="text-sm text-[#78716C] mb-5">Genauigkeit nach Übungstyp</p>
        {skills.length > 0 ? (
          <div className="space-y-4">
            {skills.sort((a, b) => b.total - a.total).map((s, i) => (
              <SkillBar key={i} label={s.skill} accuracy={s.accuracy} total={s.total} />
            ))}
          </div>
        ) : (
          <p className="text-[#78716C] text-sm text-center py-4">Mach ein paar Übungen, um deine Fähigkeiten zu sehen!</p>
        )}
      </div>

      {/* Activity Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-5 text-center">
          <ChatCircle size={28} className="mx-auto text-[#3B82F6] mb-2" weight="duotone" />
          <p className="text-2xl font-bold text-[#292524]">{summary.posts_count}</p>
          <p className="text-sm text-[#78716C]">Forum-Beiträge</p>
        </div>
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-5 text-center">
          <Microphone size={28} className="mx-auto text-[#8B5CF6] mb-2" weight="duotone" />
          <p className="text-2xl font-bold text-[#292524]">{summary.voice_count}</p>
          <p className="text-sm text-[#78716C]">Sprachaufnahmen</p>
        </div>
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-5 text-center">
          <Robot size={28} className="mx-auto text-[#AB1C24] mb-2" weight="duotone" />
          <p className="text-2xl font-bold text-[#292524]">{summary.ai_conversations}</p>
          <p className="text-sm text-[#78716C]">KI-Gespräche</p>
        </div>
      </div>
      <LevelFigureModal
        levelInfo={modalLevelInfo}
        open={figureModalOpen}
        onClose={() => setFigureModalOpen(false)}
      />

      {/* MCER Pedagogy panel — extended analysis */}
      {pedagogy && <PedagogyPanel p={pedagogy} />}
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Pedagogy panel — CEFR-aligned insights, weak spots, recommendations
const PRIORITY_STYLE = {
  alta: { label: 'Alta', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  media: { label: 'Media', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  baja: { label: 'Baja', bg: 'bg-stone-100', text: 'text-stone-600', border: 'border-stone-200' },
};

const PedagogyPanel = ({ p }) => (
  <div className="space-y-6 mt-8" data-testid="pedagogy-panel">
    <div className="flex items-center gap-2">
      <GraduationCap size={22} weight="duotone" className="text-[#AB1C24]" />
      <h2 className="font-heading text-xl font-bold text-[#292524]">Análisis pedagógico</h2>
      <span className="text-xs text-[#78716C] ml-1">Según el MCER (A1–C2)</span>
    </div>

    {/* Macro-skill radar */}
    <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5" data-testid="macro-profile">
      <h3 className="font-heading font-semibold text-[#292524] mb-1">Perfil por competencias</h3>
      <p className="text-xs text-[#78716C] mb-4">Marco Común Europeo de Referencia: 4 macro-competencias + léxico + gramática</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {p.macro_profile.map(m => {
          const band = p.band_copy?.[m.band] || { label: m.band, color: '#78716C', hint: '' };
          const pct = m.activity_only ? Math.min(100, (m.total * 10)) : (m.accuracy ?? 0);
          return (
            <div key={m.skill} className="rounded-xl border border-[#E7E5E4] p-3" data-testid={`macro-${m.skill}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-[#292524]">{m.label}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: band.color, backgroundColor: `${band.color}14` }}>
                  {band.label}
                </span>
              </div>
              <div className="h-2 bg-[#F5F5F4] rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: band.color }} />
              </div>
              <p className="text-xs text-[#78716C] mt-1.5">
                {m.activity_only
                  ? `${m.total} grabaciones · ${band.hint}`
                  : `${m.correct}/${m.total} aciertos · ${m.accuracy}% · ${band.hint}`}
              </p>
            </div>
          );
        })}
      </div>
    </div>

    {/* Level mastery ladder */}
    <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5" data-testid="level-mastery">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading font-semibold text-[#292524]">Escala MCER</h3>
          <p className="text-xs text-[#78716C]">Dominio por nivel · umbral de avance: 80% global + 100 aciertos</p>
        </div>
        {p.ready_for_next && p.next_level && (
          <span className="text-xs font-semibold bg-green-100 text-green-800 border border-green-200 px-3 py-1.5 rounded-full flex items-center gap-1">
            <ArrowRight size={14} weight="bold" /> Listo para {p.next_level}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {p.level_mastery.map(l => {
          const band = p.band_copy?.[l.band] || { color: '#78716C', label: '' };
          const isCurrent = l.level === p.current_level;
          return (
            <div
              key={l.level}
              className={`rounded-xl border p-3 text-center ${isCurrent ? 'border-[#AB1C24] ring-2 ring-[#AB1C24]/10' : 'border-[#E7E5E4]'}`}
              data-testid={`level-row-${l.level}`}
            >
              <p className="font-heading font-bold text-lg" style={{ color: band.color }}>{l.level}</p>
              <p className="text-[11px] text-[#78716C]">{l.total} ej · {l.accuracy}%</p>
              {isCurrent && <p className="text-[10px] font-semibold text-[#AB1C24] mt-1">Tu nivel</p>}
            </div>
          );
        })}
      </div>
    </div>

    {/* Weak spots — most missed items */}
    {p.weak_spots && p.weak_spots.length > 0 && (
      <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5" data-testid="weak-spots">
        <h3 className="font-heading font-semibold text-[#292524] mb-1">Puntos débiles concretos</h3>
        <p className="text-xs text-[#78716C] mb-4">Los 5 ítems que más has fallado — revísalos con tu profesor</p>
        <ul className="space-y-2">
          {p.weak_spots.map((w, i) => (
            <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#FAF9F6] border border-[#E7E5E4]" data-testid={`weak-${i}`}>
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-sm font-bold">×{w.misses}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#292524] truncate">{w.question || '(ejercicio sin enunciado)'}</p>
                <p className="text-xs text-[#78716C] mt-0.5">
                  <span className="font-semibold">Respuesta correcta:</span> {w.correct_answer || '—'}
                  {w.level && <span className="ml-2">· {w.level}</span>}
                  {w.exercise_type && <span className="ml-2">· {SKILL_LABELS[w.exercise_type] || w.exercise_type}</span>}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Recommendations */}
    {p.recommendations && p.recommendations.length > 0 && (
      <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5" data-testid="pedagogy-recs">
        <h3 className="font-heading font-semibold text-[#292524] mb-1">Recomendaciones personalizadas</h3>
        <p className="text-xs text-[#78716C] mb-4">Basadas en los descriptores del MCER y tu perfil actual</p>
        <div className="space-y-3">
          {p.recommendations.map((r, i) => {
            const st = PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.media;
            return (
              <div key={i} className={`rounded-xl border ${st.border} ${st.bg} p-4`} data-testid={`rec-${i}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${st.text}`}>{st.label}</span>
                      <span className="text-xs text-[#78716C]">·</span>
                      <span className="text-xs text-[#78716C]">{r.skill}</span>
                    </div>
                    <p className="font-semibold text-[#292524]">{r.title}</p>
                    <p className="text-sm text-[#57534E] mt-1">{r.detail}</p>
                  </div>
                  {r.action && (
                    <a href={r.action.path} className="flex-shrink-0 self-start inline-flex items-center gap-1 text-sm font-semibold text-[#AB1C24] hover:underline whitespace-nowrap">
                      {r.action.label} <ArrowRight size={14} weight="bold" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

export default LernanalysePage;
